import React, { useState } from "react";
import { supabase } from "../../../lib/supabase";
import { X, Send, AlertCircle, HelpCircle } from "lucide-react";
import { toast } from "react-toastify";

const CATEGORIES = [
  "Teachers",
  "Academic",
  "Registration",
  "General",
  "Hostel",
  "Other",
];

const QAPostForm = ({ onClose, userId, userAlias, userCampus }) => {
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("");
  const [isPosting, setIsPosting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim() || !category) return;

    setIsPosting(true);
    const { error } = await supabase.from("posts").insert([
      {
        content: content.trim(),
        category,
        community_id: 2, // Campus Q&A
        author_id: userId,
        author_alias: userAlias,
        campus_code: userCampus, // TAG QUESTION WITH CAMPUS
      },
    ]);

    if (error) {
      toast.error("Failed to post question");
      setIsPosting(false);
    } else {
      toast.success("Question posted!");
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-500">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" 
        onClick={onClose}
      />
 
      {/* Modal Container */}
      <div className="relative bg-white dark:bg-[#050507] w-full max-w-2xl h-[95vh] sm:h-auto sm:max-h-[85vh] sm:rounded-[2.5rem] rounded-t-[2.5rem] shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-full duration-700 transition-all border border-slate-100 dark:border-white/5">
        
        {/* Header - Matching QAThread Style */}
        <div className="p-6 border-b border-slate-100 dark:border-white/5 flex items-center justify-between bg-white/80 dark:bg-[#050507]/80 backdrop-blur-2xl sticky top-0 z-20">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-2xl">
              <HelpCircle size={20} />
            </div>
            <div>
              <h3 className="font-black text-xl text-slate-900 dark:text-white tracking-tight">Ask Question</h3>
              <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-0.5">
                {userCampus} HUB
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-3 hover:bg-slate-100 dark:hover:bg-white/10 rounded-2xl transition-all text-slate-400"
          >
            <X size={20} />
          </button>
        </div>
 
        {/* Form Content - Scrollable */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto custom-scrollbar p-6 md:p-8 space-y-10 bg-transparent"
        >
          {/* Category Selection */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <h4 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">
                Select Category
              </h4>
              <div className="flex-1 h-[1px] bg-slate-100 dark:bg-white/5"></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategory(cat)}
                  className={`px-4 py-3 rounded-2xl text-[11px] sm:text-xs font-black transition-all uppercase tracking-widest border-2 ${
                    category === cat
                      ? "bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-500/30"
                      : "bg-slate-50 dark:bg-white/5 text-slate-400 dark:text-slate-500 border-transparent hover:border-slate-200 dark:hover:border-white/10"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
 
          {/* Question Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <h4 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">
                Your Inquiry
              </h4>
              <div className="flex-1 h-[1px] bg-slate-100 dark:bg-white/5"></div>
            </div>
            <textarea
              required
              placeholder="What's your question? Be specific..."
              className="w-full min-h-[160px] bg-slate-50 dark:bg-slate-900 border border-transparent rounded-[1.5rem] p-5 text-sm sm:text-base text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-600 outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all resize-none font-medium leading-relaxed"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>
 
          {/* Identity Information Banner */}
          <div className="bg-indigo-50/50 dark:bg-indigo-900/20 border border-indigo-100/50 dark:border-indigo-800/30 p-5 rounded-[1.5rem] flex gap-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <div className="shrink-0 pt-0.5">
              <AlertCircle size={18} className="text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-[10px] font-black text-indigo-700 dark:text-indigo-400 uppercase tracking-widest mb-1">
                Verified Identity
              </p>
              <p className="text-xs text-indigo-900/70 dark:text-indigo-300/70 font-medium">
                You are posting as <span className="font-black text-indigo-900 dark:text-white">{userAlias}</span>. Professionalism is encouraged in the academic hub.
              </p>
            </div>
          </div>
        </form>
 
        {/* Footer - Matching QAThread Style */}
        <div className="p-8 bg-white dark:bg-[#050507] border-t border-slate-100 dark:border-white/5 space-y-4">
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={!content.trim() || !category || isPosting}
            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-4 sm:py-5 rounded-[1.5rem] font-black text-xs uppercase tracking-[0.2em] transition-all shadow-xl shadow-indigo-600/20 active:scale-[0.98] flex items-center justify-center gap-3"
          >
            {isPosting ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Publishing...</span>
              </>
            ) : (
              <>
                <Send size={18} />
                <span>Submit Question</span>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full text-center text-[10px] font-black text-slate-400 hover:text-slate-600 dark:hover:text-slate-500 uppercase tracking-[0.2em] transition-colors"
          >
            Discard Question
          </button>
        </div>
      </div>
    </div>
  );
};

export default QAPostForm;
